import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Cylinder, DollarSign, Settings, X, Check, Edit2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { oilFieldTanksDatabase } from '../data/materials';
import { toast } from 'sonner';

const TanksCalculator = ({ tankMaterials, setTankMaterials }) => {
  const [selectedTank, setSelectedTank] = useState('');
  const [quantity, setQuantity] = useState('');
  const [costPerTon, setCostPerTon] = useState('');

  // Custom tank management
  const [customTanks, setCustomTanks] = useState(() => {
    const saved = localStorage.getItem('customTankTypes');
    return saved ? JSON.parse(saved) : [];
  });
  const [showManageTanks, setShowManageTanks] = useState(false);
  const [showAddTank, setShowAddTank] = useState(false);
  const [newTank, setNewTank] = useState({
    name: '',
    capacity: '',
    weightPerTank: '',
    costPerTon: ''
  });
  const [editingTank, setEditingTank] = useState(null);

  // All tanks (database + custom)
  const allTanks = [...oilFieldTanksDatabase, ...customTanks.map(t => ({
    ...t,
    category: 'Custom',
    description: `Custom tank: ${t.name}`
  }))];

  // Save custom tanks to localStorage
  useEffect(() => {
    localStorage.setItem('customTankTypes', JSON.stringify(customTanks));
  }, [customTanks]);

  const handleAddTank = () => {
    if (!selectedTank || !quantity || quantity <= 0) {
      toast.error('Please select a tank and enter quantity');
      return;
    }

    const tankData = allTanks.find(t => t.name === selectedTank);
    if (!tankData) return;

    const qty = parseInt(quantity);
    const totalWeight = tankData.weightPerTank * qty;
    const totalTons = totalWeight / 2000;
    const cost = parseFloat(costPerTon) || tankData.costPerTon || 0;
    const totalCost = totalTons * cost;

    const newTankEntry = {
      id: Date.now(),
      name: tankData.name,
      category: tankData.category || 'Custom',
      capacity: tankData.capacity,
      quantity: qty,
      weightPerTank: tankData.weightPerTank,
      totalWeight: totalWeight,
      totalTons: totalTons,
      costPerTon: cost,
      totalCost: totalCost,
      addedAt: new Date().toISOString()
    };

    setTankMaterials([...tankMaterials, newTankEntry]);
    toast.success(`Added ${qty} ${tankData.name}`);
    
    // Reset form
    setSelectedTank('');
    setQuantity('');
    setCostPerTon('');
  };

  const handleRemove = (id) => {
    setTankMaterials(tankMaterials.filter(tank => tank.id !== id));
    toast.success('Tank removed');
  };

  // Custom tank management
  const handleAddCustomTank = () => {
    if (!newTank.name || !newTank.weightPerTank) {
      toast.error('Please enter tank name and weight');
      return;
    }

    const customTank = {
      id: Date.now(),
      name: newTank.name.trim(),
      capacity: newTank.capacity || 'Custom',
      weightPerTank: parseFloat(newTank.weightPerTank),
      costPerTon: parseFloat(newTank.costPerTon) || 0
    };

    setCustomTanks([...customTanks, customTank]);
    setNewTank({ name: '', capacity: '', weightPerTank: '', costPerTon: '' });
    setShowAddTank(false);
    toast.success(`Custom tank "${customTank.name}" added`);
  };

  const handleRemoveCustomTank = (id) => {
    setCustomTanks(customTanks.filter(t => t.id !== id));
    toast.success('Custom tank removed');
  };

  const handleUpdateCustomTank = (id) => {
    if (!editingTank.name || !editingTank.weightPerTank) {
      toast.error('Please enter tank name and weight');
      return;
    }
    setCustomTanks(customTanks.map(t => t.id === id ? { ...editingTank } : t));
    setEditingTank(null);
    toast.success('Custom tank updated');
  };

  const totals = tankMaterials.reduce((acc, tank) => ({
    totalQuantity: acc.totalQuantity + tank.quantity,
    totalWeight: acc.totalWeight + tank.totalWeight,
    totalTons: acc.totalTons + (tank.totalTons || tank.totalWeight / 2000),
    totalCost: acc.totalCost + (tank.totalCost || 0)
  }), { totalQuantity: 0, totalWeight: 0, totalTons: 0, totalCost: 0 });

  return (
    <div className="space-y-6">
      {/* Manage Custom Tanks */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Manage Custom Tank Types
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowManageTanks(!showManageTanks)}
            >
              {showManageTanks ? 'Hide' : 'Manage'}
            </Button>
          </CardTitle>
        </CardHeader>
        {showManageTanks && (
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Add custom tank types with specific capacities and weights.
            </p>

            {/* Add New Tank Form */}
            {showAddTank ? (
              <div className="grid grid-cols-5 gap-2 p-3 bg-muted/30 rounded-lg">
                <Input
                  placeholder="Tank name"
                  value={newTank.name}
                  onChange={(e) => setNewTank({...newTank, name: e.target.value})}
                  className="h-9"
                />
                <Input
                  placeholder="Capacity (gal)"
                  value={newTank.capacity}
                  onChange={(e) => setNewTank({...newTank, capacity: e.target.value})}
                  className="h-9"
                />
                <Input
                  type="number"
                  placeholder="Weight (lbs)"
                  value={newTank.weightPerTank}
                  onChange={(e) => setNewTank({...newTank, weightPerTank: e.target.value})}
                  className="h-9"
                />
                <Input
                  type="number"
                  placeholder="Cost/ton"
                  value={newTank.costPerTon}
                  onChange={(e) => setNewTank({...newTank, costPerTon: e.target.value})}
                  className="h-9"
                />
                <div className="flex gap-1">
                  <Button size="sm" onClick={handleAddCustomTank} className="h-9 px-3">
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowAddTank(false)} className="h-9 px-3">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowAddTank(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Custom Tank
              </Button>
            )}

            {/* Custom Tanks List */}
            {customTanks.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Custom Tanks ({customTanks.length})</Label>
                {customTanks.map(tank => (
                  <div key={tank.id} className="flex items-center justify-between p-2 bg-background rounded border">
                    {editingTank?.id === tank.id ? (
                      <div className="flex-1 grid grid-cols-5 gap-2">
                        <Input
                          value={editingTank.name}
                          onChange={(e) => setEditingTank({...editingTank, name: e.target.value})}
                          className="h-8"
                        />
                        <Input
                          value={editingTank.capacity}
                          onChange={(e) => setEditingTank({...editingTank, capacity: e.target.value})}
                          className="h-8"
                        />
                        <Input
                          type="number"
                          value={editingTank.weightPerTank}
                          onChange={(e) => setEditingTank({...editingTank, weightPerTank: e.target.value})}
                          className="h-8"
                        />
                        <Input
                          type="number"
                          value={editingTank.costPerTon}
                          onChange={(e) => setEditingTank({...editingTank, costPerTon: e.target.value})}
                          className="h-8"
                        />
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => handleUpdateCustomTank(tank.id)} className="h-8 px-2">
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingTank(null)} className="h-8 px-2">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1">
                          <span className="font-medium">{tank.name}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            ({tank.capacity}, {(tank.weightPerTank / 1000).toFixed(1)}k lbs)
                          </span>
                          {tank.costPerTon > 0 && (
                            <Badge variant="secondary" className="ml-2">${tank.costPerTon}/ton</Badge>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditingTank(tank)} className="h-7 px-2">
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleRemoveCustomTank(tank.id)} className="h-7 px-2 text-destructive">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Add Tank Form */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            Add Oil Field Tanks
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p className="text-sm">Calculate oil field tank costs by selecting tank type and quantity. Includes weight per tank and total cost calculations. You can also add custom tank specifications.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="tank-select">Tank Type</Label>
            <Select value={selectedTank} onValueChange={setSelectedTank}>
              <SelectTrigger id="tank-select">
                <SelectValue placeholder="Select tank type..." />
              </SelectTrigger>
              <SelectContent>
                {/* Database Tanks */}
                {oilFieldTanksDatabase.map((tank) => (
                  <SelectItem key={tank.name} value={tank.name}>
                    {tank.name} - {tank.capacity} - {(tank.weightPerTank / 1000).toFixed(1)}k lbs
                  </SelectItem>
                ))}
                {/* Custom Tanks */}
                {customTanks.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted/50">Custom Tanks</div>
                    {customTanks.map((tank) => (
                      <SelectItem key={`custom-${tank.id}`} value={tank.name}>
                        {tank.name} - {tank.capacity} - {(tank.weightPerTank / 1000).toFixed(1)}k lbs (Custom)
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                placeholder="Enter number of tanks"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min="0"
                step="1"
              />
            </div>

            <div>
              <Label htmlFor="cost-per-ton" className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Cost per Ton
              </Label>
              <Input
                id="cost-per-ton"
                type="number"
                placeholder="$0.00"
                value={costPerTon}
                onChange={(e) => setCostPerTon(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <Button onClick={handleAddTank} className="w-full bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Tanks
          </Button>
        </CardContent>
      </Card>

      {/* Totals Summary */}
      {tankMaterials.length > 0 && (
        <Card className="bg-accent/10">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Total Tanks</p>
                <p className="text-2xl font-bold text-foreground">{totals.totalQuantity}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Weight</p>
                <p className="text-2xl font-bold text-accent">{totals.totalTons.toFixed(2)} tons</p>
                <p className="text-xs text-muted-foreground">{totals.totalWeight.toLocaleString()} lbs</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Cost</p>
                <p className="text-2xl font-bold text-green-600">${totals.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Cost/Ton</p>
                <p className="text-xl font-bold text-orange-600">
                  ${totals.totalTons > 0 ? (totals.totalCost / totals.totalTons).toFixed(2) : '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tank List */}
      {tankMaterials.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">Tank List ({tankMaterials.length})</h3>
          {tankMaterials.map((tank) => (
            <Card key={tank.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Cylinder className="w-4 h-4 text-primary" />
                      <h4 className="font-semibold text-foreground">{tank.name}</h4>
                      {tank.category === 'Custom' && <Badge variant="outline">Custom</Badge>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2 text-sm">
                      <div>
                        <p className="text-muted-foreground">Capacity</p>
                        <p className="font-medium">{tank.capacity}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Quantity</p>
                        <p className="font-medium">{tank.quantity}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Weight/Tank</p>
                        <p className="font-medium">{(tank.weightPerTank / 1000).toFixed(1)}k lbs</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Weight</p>
                        <p className="font-medium text-accent">{(tank.totalTons || tank.totalWeight / 2000).toFixed(2)} tons</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Cost/Ton</p>
                        <p className="font-medium">${(tank.costPerTon || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Cost</p>
                        <p className="font-medium text-green-600">${(tank.totalCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(tank.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TanksCalculator;
